import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { App as AntdApp, ConfigProvider } from 'antd';
import enUS from 'antd/es/locale/en_US';
import ruRU from 'antd/es/locale/ru_RU';
import srRS from 'antd/es/locale/sr_RS';
import plPL from 'antd/es/locale/pl_PL';
import { useThemeStore } from '@/store/useThemeStore';
import { colorToHex } from '@/utils/utils';

const locales = {
  en: enUS,
  sr: srRS,
  ru: ruRU,
  sq: enUS,
  pl: plPL,
};

/**
 * ? INFO: AntDesign dark mode
 * https://betterprogramming.pub/how-to-toggle-dark-theme-with-ant-design-5-0-eb68552f62b8
 */
export default function AntdProvider({ children }) {
  const palette = useThemeStore((state) => state.palette);

  const { i18n } = useTranslation();
  const lang = i18n.language;

  const theme = useMemo(() => {
    const styles = {
      radius: 4,
      modalRadius: 8,
    };

    const colorPrimary = colorToHex({ color: palette });

    const calendarColorPrimary = colorToHex({ color: palette, opacity: 0.15 });

    return {
      token: {
        colorPrimary: colorPrimary,
        borderRadiusLG: styles.modalRadius,
      },
      components: {
        Calendar: {
          itemActiveBg: calendarColorPrimary,
        },
        Radio: {
          colorPrimary: colorPrimary,
          colorPrimaryHover: colorPrimary,
        },
        Checkbox: {
          colorPrimary: colorPrimary,
          colorPrimaryHover: colorPrimary,
        },
        Switch: {
          colorPrimary: colorPrimary,
          colorPrimaryHover: colorPrimary,
        },
        Select: {
          borderRadius: styles.radius,
          colorPrimary: colorPrimary,
          colorPrimaryHover: colorPrimary,
          controlItemBgHover: '#f0f0f0',
          controlItemBgActive: '#ffffff',
          controlItemBgSelected: '#e6f7ff',
        },
        Input: {
          borderRadius: styles.radius,
          colorPrimary: colorPrimary,
          colorPrimaryHover: colorPrimary,
          colorBgContainer: '#fff',
          controlOutline: 'none',
          controlItemBgHover: '#f0f0f0',
          controlItemBgActive: '#ffffff',
        },
        Button: {
          borderRadius: 0,
        },
        Pagination: {
          itemBorderRadius: styles.radius,
          colorPrimary: colorPrimary,
          colorPrimaryHover: colorPrimary,
          colorLink: '#1890ff',
        },
        Tooltip: {
          colorBgSpotlight: '#000000',
          colorTextLightSolid: '#ffffff',
          borderRadius: 4,
        },
      },
    };
  }, [palette]);

  return (
    <ConfigProvider locale={locales[lang]} theme={theme}>
      <AntdApp>{children}</AntdApp>
    </ConfigProvider>
  );
}
